import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { emailSettingsService } from '../services/emailSettingsService';
import { defaultTemplates, mergeEmailSettingsWithDefaults } from '../constants/defaultTemplates';
import { validateEmailSettings } from '../utils/validation';
import { useTutorial } from '../../../../components/tutorial/TutorialContext';

const CACHE_PREFIX = 'email-settings-cache';
const SAVE_DELAY_MS = 350;

const getCacheKey = (companyId) => `${CACHE_PREFIX}:${companyId}`;

const readCachedPayload = (companyId) => {
  if (!companyId || typeof window === 'undefined') {
    return null;
  }

  try {
    const cachedValue = window.localStorage.getItem(getCacheKey(companyId));
    if (!cachedValue) {
      return null;
    }

    const parsedValue = JSON.parse(cachedValue);

    return {
      ...parsedValue,
      settings: mergeEmailSettingsWithDefaults(parsedValue?.settings || defaultTemplates)
    };
  } catch (error) {
    console.warn('Unable to read cached email settings:', error);
    return null;
  }
};

const writeCachedPayload = (companyId, payload) => {
  if (!companyId || typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(getCacheKey(companyId), JSON.stringify(payload));
  } catch (error) {
    console.warn('Unable to cache email settings:', error);
  }
};

export const useEmailSettings = (companyId) => {
  const { markStepComplete } = useTutorial();
  const cachedPayload = useMemo(() => readCachedPayload(companyId), [companyId]);
  const [settings, setSettings] = useState(() => cachedPayload?.settings || null);
  const [companyData, setCompanyData] = useState(() => cachedPayload?.companyData || null);
  const [loading, setLoading] = useState(() => Boolean(companyId) && !cachedPayload?.settings);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const isMountedRef = useRef(true);
  const settingsRef = useRef(settings);
  const companyDataRef = useRef(companyData);
  const pendingSaveRef = useRef(null);
  const saveInFlightRef = useRef(false);
  const saveTimerRef = useRef(null);

  useEffect(() => {
    settingsRef.current = settings;
  }, [settings]);

  useEffect(() => {
    companyDataRef.current = companyData;
  }, [companyData]);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;

      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
    };
  }, []);

  const flushQueuedSaves = useCallback(async () => {
    if (!companyId || saveInFlightRef.current || !pendingSaveRef.current) {
      return;
    }

    saveInFlightRef.current = true;

    while (pendingSaveRef.current) {
      const nextSettings = pendingSaveRef.current;
      pendingSaveRef.current = null;

      try {
        await emailSettingsService.updateEmailSettings(companyId, nextSettings);

        if (
          nextSettings?.reminderEnabled &&
          Array.isArray(nextSettings?.reminders) &&
          nextSettings.reminders.length > 0
        ) {
          markStepComplete(6);
        }

        if (isMountedRef.current) {
          setError(null);
        }
      } catch (err) {
        console.error(err);

        if (isMountedRef.current) {
          setError('Failed to update settings. Changes will keep trying as you continue updating.');
        }
      }
    }

    saveInFlightRef.current = false;

    if (isMountedRef.current) {
      setSaving(false);
    }
  }, [companyId, markStepComplete]);

  const scheduleSave = useCallback(() => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }

    saveTimerRef.current = setTimeout(() => {
      saveTimerRef.current = null;
      void flushQueuedSaves();
    }, SAVE_DELAY_MS);
  }, [flushQueuedSaves]);

  const fetchSettings = useCallback(async ({ background = false } = {}) => {
    if (!companyId) {
      return;
    }

    try {
      if (!background && !settingsRef.current) {
        setLoading(true);
      }

      const data = await emailSettingsService.getEmailSettings(companyId);

      const { companyData: company, ...emailSettings } = data;
      const normalizedSettings = mergeEmailSettingsWithDefaults(emailSettings || defaultTemplates);
      const normalizedCompanyData = company || {};
      const hasPendingLocalChanges = Boolean(pendingSaveRef.current) || saveInFlightRef.current;

      if (isMountedRef.current) {
        if (!hasPendingLocalChanges) {
          writeCachedPayload(companyId, {
            settings: normalizedSettings,
            companyData: normalizedCompanyData
          });
          setSettings(normalizedSettings);
        } else {
          writeCachedPayload(companyId, {
            settings: settingsRef.current,
            companyData: normalizedCompanyData
          });
        }

        setCompanyData(normalizedCompanyData);
        setError(null);
      }
    } catch (err) {
      console.error(err);

      if (isMountedRef.current && !settingsRef.current) {
        setError('Failed to load email settings');
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [companyId]);

  useEffect(() => {
    if (!companyId) {
      pendingSaveRef.current = null;
      setSettings(null);
      setCompanyData(null);
      setLoading(false);
      setError(null);
      return;
    }

    const nextCachedPayload = readCachedPayload(companyId);

    pendingSaveRef.current = null;

    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }

    settingsRef.current = nextCachedPayload?.settings || null;
    companyDataRef.current = nextCachedPayload?.companyData || null;
    setSettings(nextCachedPayload?.settings || null);
    setCompanyData(nextCachedPayload?.companyData || null);
    setLoading(!nextCachedPayload?.settings);
    setError(null);

    void fetchSettings({ background: Boolean(nextCachedPayload?.settings) });
  }, [companyId, fetchSettings]);

  const updateSettings = async (newSettings) => {
    if (!companyId) {
      return false;
    }

    try {
      const errors = validateEmailSettings(newSettings);
      if (Object.keys(errors).length > 0) {
        throw new Error('Invalid settings');
      }

      settingsRef.current = newSettings;
      pendingSaveRef.current = newSettings;

      writeCachedPayload(companyId, {
        settings: newSettings,
        companyData: companyDataRef.current || {}
      });

      if (isMountedRef.current) {
        setSettings(newSettings);
        setError(null);
        setSaving(true);
      }

      scheduleSave();
      return true;
    } catch (err) {
      setError('Failed to update settings');
      console.error(err);
      return false;
    }
  };

  const sendTestEmail = async (templateType, testEmail) => {
    try {
      await emailSettingsService.sendTestEmail(companyId, templateType, testEmail);
      return true;
    } catch (err) {
      console.error('Error sending test email:', err);
      return false;
    }
  };

  return {
    settings,
    companyData,
    loading,
    error,
    saving,
    updateSettings,
    sendTestEmail,
    refreshSettings: fetchSettings
  };
}; 